import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { 
  Bell, Mail, Clock, Loader2, Send, CheckCircle, AlertTriangle, 
  RefreshCw, Calendar
} from 'lucide-react';
import { toast } from 'sonner';

export function TrialReminders({ api }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await api().get('/admin/trial-reminder-status');
      setStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch trial reminder status:', error);
      toast.error('Failed to load trial reminder status');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleSendReminders = async () => {
    setSending(true);
    try {
      const response = await api().post('/admin/send-trial-reminders');
      const result = response.data;
      
      if (result.reminders_sent > 0) {
        toast.success(`Sent ${result.reminders_sent} reminder email(s)`);
      } else if (result.already_reminded > 0) {
        toast.info('All eligible reminders have already been sent');
      } else {
        toast.info('No forums require reminders at this time');
      }
      
      // Refresh status
      await fetchStatus();
    } catch (error) {
      console.error('Failed to send reminders:', error);
      toast.error('Failed to send reminders');
    } finally {
      setSending(false);
    }
  };

  const getDaysLeftBadge = (daysLeft) => {
    if (daysLeft <= 1) {
      return <Badge variant="destructive" className="gap-1"><AlertTriangle className="h-3 w-3" /> {daysLeft} day</Badge>;
    } else if (daysLeft <= 3) {
      return <Badge className="bg-orange-500 gap-1"><Clock className="h-3 w-3" /> {daysLeft} days</Badge>;
    } else if (daysLeft <= 7) {
      return <Badge className="bg-yellow-500 text-yellow-900 gap-1"><Clock className="h-3 w-3" /> {daysLeft} days</Badge>;
    } else {
      return <Badge variant="secondary" className="gap-1"><Clock className="h-3 w-3" /> {daysLeft} days</Badge>;
    }
  };

  const getRemindersBadges = (reminders) => {
    if (!reminders || reminders.length === 0) {
      return <span className="text-muted-foreground text-sm">None sent</span>;
    }
    
    return (
      <div className="flex gap-1 flex-wrap">
        {reminders.includes('day_7') && (
          <Badge variant="outline" className="text-xs gap-1">
            <CheckCircle className="h-3 w-3 text-green-500" /> 7-day
          </Badge>
        )}
        {reminders.includes('day_3') && (
          <Badge variant="outline" className="text-xs gap-1">
            <CheckCircle className="h-3 w-3 text-green-500" /> 3-day
          </Badge>
        )}
        {reminders.includes('day_1') && (
          <Badge variant="outline" className="text-xs gap-1">
            <CheckCircle className="h-3 w-3 text-green-500" /> 1-day
          </Badge>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="trial-reminders">
      {/* Overview Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total in Trial</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{status?.total_in_trial || 0}</div>
            <p className="text-xs text-muted-foreground">forums on free trial</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Expiring Soon</CardTitle>
            <AlertTriangle className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{status?.total_expiring_soon || 0}</div>
            <p className="text-xs text-muted-foreground">expiring in 14 days</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Send Reminders</CardTitle>
            <Mail className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <Button 
              onClick={handleSendReminders} 
              disabled={sending}
              className="w-full gap-2"
              data-testid="send-reminders-btn"
            >
              {sending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  Send Due Reminders
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Expiring Soon Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Forums Expiring Soon
              </CardTitle>
              <CardDescription>
                Trials expiring in the next 14 days. Reminders are sent at 7, 3, and 1 day(s) before expiration.
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={fetchStatus} className="gap-2">
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {status?.expiring_soon?.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500/50" />
              <h3 className="font-medium mb-2">All Clear!</h3>
              <p className="text-muted-foreground text-sm">
                No forums are expiring in the next 14 days.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Forum</TableHead>
                  <TableHead>Chair Email</TableHead>
                  <TableHead>Members</TableHead>
                  <TableHead>Days Left</TableHead>
                  <TableHead>Reminders Sent</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {status?.expiring_soon?.map((forum) => (
                  <TableRow key={forum.forum_id}>
                    <TableCell className="font-medium">{forum.forum_name}</TableCell>
                    <TableCell className="text-sm">{forum.chair_email || 'N/A'}</TableCell>
                    <TableCell>{forum.member_count}</TableCell>
                    <TableCell>{getDaysLeftBadge(forum.days_left)}</TableCell>
                    <TableCell>{getRemindersBadges(forum.reminders_sent)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Reminder Schedule Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Reminder Schedule</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
              <div className="p-2 rounded-full bg-yellow-500/20">
                <Mail className="h-4 w-4 text-yellow-600" />
              </div>
              <div>
                <p className="font-medium text-yellow-700 dark:text-yellow-400">7 Days Before</p>
                <p className="text-sm text-muted-foreground">First reminder</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-orange-500/10 border border-orange-500/20">
              <div className="p-2 rounded-full bg-orange-500/20">
                <Mail className="h-4 w-4 text-orange-600" />
              </div>
              <div>
                <p className="font-medium text-orange-700 dark:text-orange-400">3 Days Before</p>
                <p className="text-sm text-muted-foreground">Second reminder</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
              <div className="p-2 rounded-full bg-red-500/20">
                <Mail className="h-4 w-4 text-red-600" />
              </div>
              <div>
                <p className="font-medium text-red-700 dark:text-red-400">1 Day Before</p>
                <p className="text-sm text-muted-foreground">Final reminder</p>
              </div>
            </div>
          </div>
          <p className="text-sm text-muted-foreground mt-4">
            Click "Send Due Reminders" to send all pending reminder emails. This should be run daily via a scheduled task.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
